<template>
  <v-container fluid>
    <v-row>
      <!-- ================= LEFT : BASIC INFO ================= -->
      <v-col md="3" cols="12">
        <v-expansion-panels v-model="panel">
          <v-expansion-panel>
            <v-expansion-panel-title
              color="blue-grey-lighten-4"
              class="panel-header"
            >
              Basic Info
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <v-form class="mt-3">
                <v-text-field
                  label="Invoice No *"
                  density="compact"
                  variant="outlined"
                  bg-color="grey-lighten-4"
                  v-model="invoice_no.value.value"
                  :error-messages="invoice_no.errorMessage.value"
                  clearable
                />

                <v-date-input
                  input-format="dd/mm/yyyy"
                  label="Invoice Date *"
                  density="compact"
                  variant="outlined"
                  bg-color="grey-lighten-4"
                  prepend-icon=""
                  v-model="invoice_date.value.value"
                  :error-messages="invoice_date.errorMessage.value"
                />

                <v-autocomplete
                  label="Customer *"
                  variant="outlined"
                  density="compact"
                  bg-color="grey-lighten-4"
                  :items="getCustomerList"
                  :item-title="item => `${item.company_name} - ${item.code}`"
                  item-value="id"
                  v-model="customer_id.value.value"
                  :error-messages="customer_id.errorMessage.value"
                  clearable
                />

                <v-autocomplete
                  label="Currency *"
                  variant="outlined"
                  density="compact"
                  bg-color="grey-lighten-4"
                  :items="getCurrencyList"
                  :item-title="item => `${item.code} - ${item.name}`"
                  item-value="id"
                  v-model="currency_id.value.value"
                  :error-messages="currency_id.errorMessage.value"
                  clearable
                />

                <v-textarea
                  label="Remarks"
                  density="compact"
                  variant="outlined"
                  bg-color="grey-lighten-4"
                  v-model="remarks.value.value"
                  rows="3"
                  clearable
                />
              </v-form>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>

      <!-- ================= RIGHT : LC + PRODUCTS ================= -->
      <v-col md="9" cols="12">
        <v-expansion-panels v-model="panel2">
          <v-expansion-panel>
            <v-expansion-panel-title
              color="blue-grey-lighten-4"
              class="panel-header"
            >
              LC Products
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <!-- LC SELECT -->
              <v-autocomplete
                class="mt-3"
                label="Master LC"
                variant="outlined"
                density="compact"
                bg-color="grey-lighten-4"
                :items="getLcExportLcMasterList"
                :item-title="item => item.code"
                item-value="id"
                v-model="selectedLcId"
                @update:modelValue="onLcChange"
                clearable
              />

              <!-- PRODUCTS TABLE -->
              <v-table density="compact" class="mt-4">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>UOM</th>
                    <th>HS Code</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Rate</th>
                    <th class="text-right">Total</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(item, index) in productList" :key="item.product_id">
                    <td>{{ index + 1 }}</td>
                    <td>{{ item.product_name }}</td>
                    <td>{{ item.uom }}</td>
                    <td>
                      <v-text-field
                        density="compact"
                        variant="outlined"
                        v-model="item.hs_code"
                        hide-details
                      />
                    </td>
                    <td class="text-right">
                      <v-text-field
                        density="compact"
                        variant="outlined"
                        type="number"
                        min="0"
                        v-model.number="item.qty"
                        @update:modelValue="recalc(item)"
                        hide-details
                      />
                    </td>
                    <td class="text-right">
                      <v-text-field
                        density="compact"
                        variant="outlined"
                        type="number"
                        min="0"
                        v-model.number="item.rate"
                        @update:modelValue="recalc(item)"
                        hide-details
                      />
                    </td>
                    <td class="text-right">
                      {{ item.total }}
                    </td>
                  </tr>
                </tbody>

                <tfoot v-if="productList.length">
                  <tr>
                    <td colspan="6" class="text-right font-weight-bold">
                      Grand Total
                    </td>
                    <td class="text-right font-weight-bold">
                      {{ grandTotal }}
                    </td>
                  </tr>
                </tfoot>
              </v-table>

              <!-- ACTION BUTTON -->
              <div class="d-flex justify-end mt-4" style="gap: 12px">
                <v-btn
                  class="bg-grey-darken-1"
                  :loading="loading"
                  @click="submitForm(0)"
                >
                  DRAFT
                </v-btn>
                <v-btn
                  class="bg-primary"
                  :loading="loading"
                  @click="submitForm(1)"
                >
                  POST
                </v-btn>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import formattedDate from "@/plugins/dateFormatter";
import { useField, useForm } from "vee-validate";
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import { useStore } from "vuex";
import * as yup from "yup";
import { useRoute } from "vue-router";

const route = useRoute();
const store = useStore();
const router = useRouter();
const isEdit = computed(() => !!route.params.id);

const { handleSubmit } = useForm({
  validationSchema: yup.object({
    invoice_no: yup.string().required(),
    invoice_date: yup.date().required(),
    customer_id: yup.number().required(),
    currency_id: yup.number().required(),
  }),
});

const invoice_no = useField("invoice_no");
const invoice_date = useField("invoice_date");
const customer_id = useField("customer_id");
const currency_id = useField("currency_id");
const remarks = useField("remarks");
const status = useField("status");
const commercial_invoice_master_id = ref(null);

const panel = ref([0]);
const panel2 = ref([0]);
const loading = ref(false);
const selectedLcId = ref(null);

const productList = ref([]);

const getCustomerList = computed(() => store.getters.getCustomerList || []);
const getCurrencyList = computed(() => store.getters.getCurrencyList || []);
const getLcExportLcMasterList = computed(  () => store.getters.getLcExportLcMasterList || []);

async function getExportCommercialInvoice(){
    if (!isEdit.value) return;
    
    loading.value = true;
    try {
        
        const res = await store.dispatch("getExportLcCommercialInvoiceById", route.params.id);
        const data = res;
        commercial_invoice_master_id.value = data.id;
    
        invoice_no.value.value = data.invoice_no;
        invoice_date.value.value = data.invoice_date;
        customer_id.value.value = data.customer_id;
        currency_id.value.value = data.currency_id;
        remarks.value.value = data.remarks || "";
        status.value.value = data.status;
    
        selectedLcId.value = data.lc_id;
        onLcChange(data.lc_id);

        productList.value = data.details.map((d) => ({
            product_id: d.product_id,
            product_name: d.product?.name || "",
            uom: d.uom || "",
            hs_code: d.hs_code || "",
            qty: d.qty,
            rate: d.unit_price,
            total: d.qty * d.unit_price,
        }));
    } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load Commercial Invoice data");
    } finally {
        loading.value = false;
    }
}

const onLcChange = (lcId) => {
  productList.value = [];
  if (!lcId) return;

  const lc = getLcExportLcMasterList.value.find((i) => i.id === lcId);
  if (!lc || !Array.isArray(lc.pis)) return;

  const map = new Map();

  lc.pis.forEach((pi) => {
    pi.details?.forEach((d) => {
      if (!map.has(d.product_id)) {
        const qty = Number(d.qty) || 1;
        const rate = Number(d.unit_price) || 0;
        map.set(d.product_id, {
          product_id: d.product_id,
          product_name: d.product?.name || "",
          uom: d.uom || "",
          hs_code: "",
          qty,
          rate,
          total: qty * rate,
        });
      }
    });
  });

  productList.value = [...map.values()];
};

const recalc = (item) => {
  const qty = Number(item.qty) || 0;
  const rate = Number(item.rate) || 0;
  item.total = qty * rate;
};

const grandTotal = computed(() =>
  productList.value.reduce((sum, item) => sum + (Number(item.total) || 0), 0)
);

const submitForm = (btnStatus) => {
  status.value.value = btnStatus;
  submit();
};

const submit = handleSubmit(async () => {
  if (!selectedLcId.value) {
    toast.error("Please select Master LC");
    return;
  }

  if (!productList.value.length) {
    toast.error("No product found from selected LC");
    return;
  }

  loading.value = true;
  try {
    await store.dispatch("saveUpdateLcExportCommercialInvoice", {
      commercial_invoice_master_id: commercial_invoice_master_id.value,
      lc_id: selectedLcId.value,
      invoice_no: invoice_no.value.value,
      invoice_date: formattedDate(invoice_date.value.value),
      customer_id: customer_id.value.value,
      currency_id: currency_id.value.value,
      remarks: remarks.value.value || null,
      status: status.value.value,
      details: productList.value.map((p) => ({
        product_id: p.product_id,
        hs_code: p.hs_code,
        qty: p.qty,
        unit_price: p.rate,
      })),
    });

    toast.success("Commercial Invoice saved successfully");
    router.push("/lc/export-commercial-invoice-record");
  } catch (error) {
    toast.error(error.response?.data?.message || "Failed to save Commercial Invoice");
  } finally {
    loading.value = false;
  }
});

onMounted(async () => {
  await store.dispatch("getCustomerList");
  await store.dispatch("getCurrencyList");
  await store.dispatch("getLcExportLcMasterList");
  getExportCommercialInvoice();
});
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>
